import { cloneDeep, isEqual, omit } from 'lodash'
import { FlowNodeTypes } from '../types'

/**
 * Fields to ignore when detecting unsaved changes for specific node types
 * These fields are set automatically by forms and shouldn't trigger unsaved changes
 */
const IGNORED_FIELDS: Partial<Record<FlowNodeTypes, string[]>> = {
  [FlowNodeTypes.ai_agent_v2]: ['channel', 'model_config'], // Set automatically based on agentMeta and defaults
  [FlowNodeTypes.ai_agent_v29]: ['channel', 'model_config'],
  [FlowNodeTypes.ai_agent_call]: ['model_config'], // Set automatically based on provider defaults
}

// Fields to ignore within skill configs based on tool type
const SKILL_CONFIG_IGNORED_FIELDS: Record<string, string[]> = {
  image_generation: ['model_id', 'provider_id'], // Keep instructions
  image_analysis: ['model_id', 'provider_id'], // Keep instructions, img_url
  audio_transcription: ['model_id', 'provider_id', 'language'], // Keep audio_url
  text_to_speech: ['model_id', 'provider_id', 'voice_id'], // Keep instructions, text
  summary: ['model_id', 'provider_id'], // Keep instructions, summary_url
}

// Filters out ignored fields from data for accurate change detection
export const filterIgnoredFields = <T extends Record<string, unknown>>(
  data: T,
  nodeType: FlowNodeTypes,
): T => {
  const fieldsToIgnore = IGNORED_FIELDS[nodeType]

  let filtered = cloneDeep(data)

  if (fieldsToIgnore && fieldsToIgnore.length > 0) {
    filtered = omit(filtered, fieldsToIgnore) as T
  }

  // Special handling for skills node
  if (nodeType === FlowNodeTypes.ai_skills) {
    const toolType = data.tool_type as string
    const filteredData = filtered as Record<string, unknown>
    // Remove auto-set/empty tool_type
    if (toolType === 'image_generation' || toolType === '') {
      delete filteredData.tool_type
    }
    // Remove all config objects except the active one
    const allConfigs = [
      'image_generation_config',
      'image_analysis_config',
      'audio_transcription_config',
      'text_to_speech_config',
      'summary_config',
    ]
    const activeConfig = toolType ? `${toolType}_config` : null
    allConfigs.forEach((config) => {
      if (config !== activeConfig) {
        delete filteredData[config]
      } else if (activeConfig && SKILL_CONFIG_IGNORED_FIELDS[toolType]) {
        // Filter auto-set fields in active config
        const configValue = filteredData[config]
        if (configValue && typeof configValue === 'object') {
          const cleanConfig = omit(
            configValue as Record<string, unknown>,
            SKILL_CONFIG_IGNORED_FIELDS[toolType],
          )
          const hasContent = Object.values(cleanConfig).some((v) => v !== '' && v != null)

          if (hasContent) {
            filteredData[config] = cleanConfig
          } else {
            delete filteredData[config]
          }
        }
      }
    })
  }

  return filtered
}

/**
 * Compares two objects after filtering out ignored fields
 */
export const compareWithIgnoredFields = <T extends Record<string, unknown>>(
  obj1: T,
  obj2: T,
  nodeType: FlowNodeTypes,
): boolean => {
  const filtered1 = filterIgnoredFields(obj1, nodeType)
  const filtered2 = filterIgnoredFields(obj2, nodeType)
  return isEqual(filtered1, filtered2)
}
